
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Bot, User, Sparkles, MessageSquare, ShieldAlert, Loader2, ArrowRight } from 'lucide-react';
import { GoogleGenAI } from "@google/genai";

declare var process: any;

const QUICK_QUERIES = [
  "Substance requirements for UAE Free Zones?",
  "Audit trail preservation under Ind-AS?",
  "ICFR hardening for multi-national entities?",
  "UAE Corporate Tax qualifying income rules?"
];

const AIAssistant: React.FC = () => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<{ role: 'user' | 'assistant', content: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  const handleQuery = async (queryText: string) => {
    if (!queryText.trim() || isLoading) return;

    const userMessage = queryText.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: userMessage,
        config: {
          systemInstruction: `You are the Institutional Regulatory Framework Assistant for CA Manthan Paliwal. 
          Your persona is: Authoritative, surgical, and professional. 
          Expertise: Indian Statutory Audit, Ind-AS, UAE Corporate Tax (Public & Private Clarifications), Global Multi-currency Controllership, and ICFR Frameworks.
          Goal: Provide high-resolution technical insights. 
          Constraint: Keep answers concise but highly technical. If asked about legal advice, clarify that you provide operational compliance screening and suggest a direct fiduciary consultation with CA Manthan Paliwal.
          Branding: Always refer to the firm as MP Strategic Compliance.`,
        },
      });

      const assistantText = response.text || "I'm sorry, I couldn't synthesize a professional response at this moment. Please try again.";
      setMessages(prev => [...prev, { role: 'assistant', content: assistantText }]);
    } catch (error) {
      console.error("AI Assistant Error:", error);
      setMessages(prev => [...prev, { role: 'assistant', content: "Protocol error: Unable to reach the statutory intelligence engine." }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="py-24 bg-slate-50 border-t border-slate-200 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_0%,_rgba(79,70,229,0.05),_transparent_50%)] pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-600 text-[10px] font-black uppercase tracking-widest mb-6 shadow-sm">
                <Sparkles size={14} className="animate-pulse" />
                Institutional Regulatory Framework v1.0
              </div>
              <h2 className="font-display font-black text-4xl md:text-5xl text-slate-900 mb-6 tracking-tight leading-tight">
                Statutory <br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-indigo-700 text-6xl">Intelligence Engine</span>
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-10 font-medium">
                Query our statutory intelligence core for technical insights on Indian or UAE regulatory hardening.
              </p>

              <div className="space-y-4">
                <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-4">Initialize Technical Protocols:</h4>
                <div className="grid grid-cols-1 gap-3">
                  {QUICK_QUERIES.map((q, i) => (
                    <button 
                      key={i}
                      onClick={() => handleQuery(q)}
                      className="text-left p-4 rounded-2xl bg-white border border-slate-200 text-slate-600 text-sm font-bold hover:border-indigo-200 hover:text-indigo-600 hover:shadow-lg transition-all flex items-center justify-between group"
                    >
                      {q}
                      <ArrowRight size={14} className="opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all text-indigo-600" />
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          <div className="lg:col-span-7">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white border border-slate-200 rounded-[3rem] shadow-2xl flex flex-col h-[600px] overflow-hidden group"
            >
              <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center text-white shadow-lg">
                    <Bot size={20} />
                  </div>
                  <div>
                    <h3 className="text-slate-900 font-bold text-sm tracking-tight leading-none">Regulatory Core Assistant</h3>
                    <p className="text-indigo-600 text-[10px] font-black uppercase tracking-widest mt-1.5 flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                      Statutory Node Active
                    </p>
                  </div>
                </div>
                <div className="px-4 py-1.5 rounded-lg bg-slate-100 border border-slate-200 text-[9px] font-black uppercase tracking-widest text-slate-500">
                  Secured Fiduciary Access
                </div>
              </div>

              <div 
                ref={scrollRef}
                className="flex-grow p-8 overflow-y-auto space-y-8 no-scrollbar scroll-smooth bg-slate-50/30"
              >
                {messages.length === 0 && (
                  <div className="h-full flex flex-col items-center justify-center text-center opacity-40">
                    <div className="w-20 h-20 rounded-3xl bg-white border border-slate-200 flex items-center justify-center mb-6 shadow-sm">
                      <MessageSquare size={32} className="text-indigo-400" />
                    </div>
                    <p className="text-slate-500 font-bold uppercase tracking-widest text-xs">Waiting for statutory query...</p>
                  </div>
                )}
                
                {messages.map((msg, i) => (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    key={i} 
                    className={`flex gap-4 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border shadow-sm ${msg.role === 'user' ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-slate-200 text-indigo-600'}`}>
                      {msg.role === 'user' ? <User size={14} /> : <Bot size={14} />}
                    </div>
                    <div className={`max-w-[85%] p-4 rounded-2xl text-sm leading-relaxed shadow-sm ${msg.role === 'user' ? 'bg-indigo-600 text-white font-medium' : 'bg-white border border-slate-200 text-slate-600 font-medium'}`}>
                      {msg.content}
                    </div>
                  </motion.div>
                ))}
                
                {isLoading && (
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center text-indigo-600 border border-slate-200 shadow-sm">
                      <Bot size={14} />
                    </div>
                    <div className="bg-white border border-slate-200 p-4 rounded-2xl flex items-center gap-2 shadow-sm">
                      <Loader2 size={16} className="animate-spin text-indigo-600" />
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Auditing Response...</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-slate-200 bg-white">
                <form 
                  onSubmit={(e) => { e.preventDefault(); handleQuery(input); }}
                  className="relative"
                >
                  <input 
                    type="text" 
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Enter technical query..."
                    className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4 pr-16 text-slate-900 text-sm outline-none focus:border-indigo-500 focus:bg-white focus:ring-4 focus:ring-indigo-50 transition-all placeholder:text-slate-400"
                  />
                  <button 
                    type="submit"
                    disabled={!input.trim() || isLoading}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:grayscale transition-all shadow-lg active:scale-95"
                  >
                    <Send size={18} />
                  </button>
                </form>
                <div className="mt-4 flex items-center justify-center gap-4 text-[9px] font-black uppercase tracking-[0.2em] text-slate-400">
                  <ShieldAlert size={10} />
                  Fiduciary Verification Protocol Active
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AIAssistant;
